import {
  ArcElement,
  Chart as ChartJS,
  ChartData,
  ChartOptions,
  Legend as ChartLegend,
  Tooltip as ChartTooltip,
} from "chart.js";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Doughnut } from "react-chartjs-2";
import {
  IoIosArrowDown,
  IoIosArrowUp,
  IoMdInformationCircle,
} from "react-icons/io";
import { TbArrowsDiagonal2, TbArrowsDiagonalMinimize } from "react-icons/tb";
import { useNavigate } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { getDashboard, getWorkTypes } from "../../api/api";
import Button from "../../components/button/Button";
import { LottieLoading } from "../../components/graphics/LottieLoading";
import CustomSelect from "../../components/select/Select";
import {
  getInitials,
  getInitialsFromFullName,
} from "../../constants/constants";
import { useAppDispatch, useAppSelector } from "../../redux/redux";
import { getAllUsers } from "../../services/AppService";
import { getLinkedFirm } from "../../sessionStorage/sessionStorage";
import { useGetDashboardStatusesQuery } from "../../slices/StatusesSlice";
import { FormSection } from "../../support/FormSection";
import { IDashboard, IWorkType, SelectOptionType } from "../../types/types";

ChartJS.register(ArcElement, ChartTooltip, ChartLegend);

export const desiredOrder = [
  "Scheduled",
  "Ready",
  "In Progress",
  "Waiting",
  "Completed",
] as const;

type DashboardStatusName = keyof IDashboard["statusCount"];

export const getDesiredStatusOrder = (statusName?: string | null) => {
  if (!statusName) {
    return Number.MAX_SAFE_INTEGER;
  }

  const index = (desiredOrder as readonly string[]).indexOf(statusName);

  return index === -1 ? Number.MAX_SAFE_INTEGER : index;
};

interface CountItem {
  name: string;
  count: number;
}

interface ChartBarItem {
  id?: string;
  name: string;
  open: number;
  overDue: number;
  color?: string;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    payload: ChartBarItem;
  }>;
}

interface StatusConfig {
  color: string;
  name: DashboardStatusName;
  mainName: string;
}

const STATUS_CONFIGS: StatusConfig[] = [
  {
    color: "#93AAFD",
    name: "Ready",
    mainName: "Ready to Begin",
  },
  {
    color: "#FFD80B",
    name: "In Progress",
    mainName: "Being Worked on",
  },
  {
    color: "#F15252",
    name: "Waiting",
    mainName: "Waiting on info",
  },
  {
    color: "#47DE96",
    name: "Completed",
    mainName: "Work Completed",
  },
];

const DEFAULT_STATUS_COUNT: IDashboard["statusCount"] = {
  Ready: 0,
  Waiting: 0,
  "In Progress": 0,
  Completed: 0,
};

const DEFAULT_DASHBOARD: IDashboard = {
  completedWorkCount: 0,
  openWorkCount: 0,
  overDueWorkCount: 0,
  assignedUserOpenWorkCount: [],
  assignedUserOverDueCount: [],
  workTypeOpenWorkCount: [],
  workTypeOverDueCount: [],
  statusCount: DEFAULT_STATUS_COUNT,
};

const normalizeName = (value: string) =>
  value.replace(/\s+/g, " ").trim().toLowerCase();

const toNumber = (value: unknown) => {
  const parsedValue = Number(value);

  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const mapCountRecord = (value: unknown): CountItem[] => {
  if (Array.isArray(value)) {
    return value.map((item) => ({
      name: String(item?.name ?? ""),
      count: toNumber(item?.count),
    }));
  }

  if (!value || typeof value !== "object") {
    return [];
  }

  return Object.entries(value as Record<string, unknown>).map(
    ([key, count]) => ({
      name: key,
      count: toNumber(count),
    }),
  );
};

const normalizeDashboardResponse = (res: any): IDashboard => ({
  statusCount: {
    Ready: toNumber(res?.statusCount?.Ready),
    Waiting: toNumber(res?.statusCount?.Waiting),
    "In Progress": toNumber(res?.statusCount?.["In Progress"]),
    Completed: toNumber(res?.statusCount?.Completed),
  },
  completedWorkCount: toNumber(res?.completedWorkCount),
  openWorkCount: toNumber(res?.openWorkCount),
  overDueWorkCount: toNumber(res?.overDueWorkCount),
  assignedUserOpenWorkCount: mapCountRecord(res?.assignedUserOpenWorkCount),
  assignedUserOverDueCount: mapCountRecord(res?.assignedUserOverDueCount),
  workTypeOpenWorkCount: mapCountRecord(res?.workTypeOpenWorkCount),
  workTypeOverDueCount: mapCountRecord(res?.workTypeOverDueCount),
});

const createCountMap = (items: CountItem[]) =>
  new Map(items.map((item) => [normalizeName(item.name), item.count]));

const getInfoMessage = (type: string) => {
  switch (type) {
    case "Current":
      return "Open work items that are not yet past due";

    case "Overdue":
      return "Open work items that are past their due date";

    case "Completed":
      return "Work items marked as completed (closed), regardless of due date";

    case "Completion Rate":
      return "Shows the percentage of all works that is completed. Completed ÷ (open + overdue + completed)";

    default:
      return "";
  }
};

const CustomTooltip: FC<CustomTooltipProps> = ({ active, payload }) => {
  const data = payload?.[0]?.payload;

  if (!active || !data) {
    return null;
  }

  return (
    <div className={"rounded-[8px] border-[1px] bg-white p-4"}>
      <p className={"font-semibold"}>{data.name}</p>

      <p className={"text-base font-semibold text-[#166AD9]"}>
        Current: {data.open}
      </p>

      <p className={"text-base font-semibold text-[#FFD80B]"}>
        Overdue: {data.overDue}
      </p>

      <p className={"text-base font-semibold"}>
        Total: {data.open + data.overDue}
      </p>
    </div>
  );
};

const InfoTooltip: FC<{ message: string }> = ({ message }) => (
  <div className={"group relative inline-block"}>
    <IoMdInformationCircle
      size={20}
      className={"cursor-pointer text-gray-400 transition hover:text-blue-500"}
    />

    <div
      className="
        pointer-events-none absolute left-1/2 top-full z-50
        mt-2 min-w-[180px] max-w-[260px] -translate-x-1/2
        scale-95 rounded-lg bg-gray-900 px-3 py-2 text-xs
        text-white opacity-0 shadow-lg transition-all duration-200
        group-hover:scale-100 group-hover:opacity-100
      ">
      {message}
    </div>
  </div>
);

const SummaryCard: FC<{
  title: string;
  value: number;
  withBorder?: boolean;
}> = ({ title, value, withBorder }) => (
  <div className={"w-1/3 py-[50px]"}>
    <div
      className={`flex flex-col items-center justify-center ${
        withBorder ? "border-r-[1px] border-[#D2D4D7]" : ""
      }`}>
      <div className={"flex items-center gap-2 text-lg"}>
        {title}
        <InfoTooltip message={getInfoMessage(title)} />
      </div>

      <div className={"text-[48px] font-bold"}>{value}</div>
    </div>
  </div>
);

export const Dashboard = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const orgId = getLinkedFirm()?.orgId;
  const { users } = useAppSelector((state) => state.appReducer);

  const [dashboard, setDashboard] = useState<IDashboard>(DEFAULT_DASHBOARD);
  const [workTypes, setWorkTypes] = useState<IWorkType[]>([]);
  const [isOpenStat, setIsOpenStat] = useState<string[]>([]);
  const [arrayFilter, setArrayFilter] = useState<{
    user: SelectOptionType | null;
    type: SelectOptionType | null;
  }>({
    user: null,
    type: null,
  });

  const { data: workTypeStatuses, isLoading: isLoadingStatuses } =
    useGetDashboardStatusesQuery(
      {
        orgId: orgId ?? "",
        userId: arrayFilter.user?.value ? String(arrayFilter.user.value) : null,
        workTypeId: arrayFilter.type?.value
          ? String(arrayFilter.type.value)
          : null,
      },
      {
        skip: !orgId,
      },
    );

  useEffect(() => {
    if (!orgId || users.length > 0) {
      return;
    }

    dispatch(getAllUsers(orgId));
  }, [dispatch, orgId, users.length]);

  useEffect(() => {
    if (!orgId) {
      return;
    }

    let isMounted = true;

    const fetchDashboard = async () => {
      try {
        const res = await getDashboard(orgId);

        if (isMounted) {
          setDashboard(normalizeDashboardResponse(res));
        }
      } catch (error) {
        console.error("Failed to fetch dashboard", error);
      }
    };

    void fetchDashboard();

    return () => {
      isMounted = false;
    };
  }, [orgId]);

  useEffect(() => {
    if (!orgId) {
      return;
    }

    let isMounted = true;

    const fetchWorkTypes = async () => {
      try {
        const res = await getWorkTypes({
          orgId,
          status: "ready",
        });

        if (isMounted) {
          setWorkTypes(res ?? []);
        }
      } catch (error) {
        console.error("Failed to fetch work types", error);
      }
    };

    void fetchWorkTypes();

    return () => {
      isMounted = false;
    };
  }, [orgId]);

  const usersOptions = useMemo<SelectOptionType[]>(
    () =>
      users.map((user) => ({
        label: `${user.userProfile.firstName} ${user.userProfile.lastName}`,
        value: user.id,
        avatar: getInitials(
          user.userProfile.firstName,
          user.userProfile.lastName,
        ),
        avatarCustomColor: user.userProfile.color,
        secondValue: user.userProfile.color,
      })),
    [users],
  );

  const workTypeOptions = useMemo<SelectOptionType[]>(
    () =>
      workTypes.map((type) => ({
        label: type.name,
        value: type.id,
      })),
    [workTypes],
  );

  const usersByName = useMemo(
    () =>
      new Map(
        users.map((user) => [
          normalizeName(
            `${user.userProfile.firstName} ${user.userProfile.lastName}`,
          ),
          user,
        ]),
      ),
    [users],
  );

  const workTypesByName = useMemo(
    () =>
      new Map(
        workTypes.map((workType) => [normalizeName(workType.name), workType]),
      ),
    [workTypes],
  );

  const assignedUserOverdueMap = useMemo(
    () => createCountMap(dashboard.assignedUserOverDueCount),
    [dashboard.assignedUserOverDueCount],
  );

  const workTypeOverdueMap = useMemo(
    () => createCountMap(dashboard.workTypeOverDueCount),
    [dashboard.workTypeOverDueCount],
  );

  const chartDataTeam = useMemo<ChartBarItem[]>(
    () =>
      dashboard.assignedUserOpenWorkCount.map((item) => {
        const user = usersByName.get(normalizeName(item.name));

        return {
          id: user?.id,
          name: item.name,
          open: item.count,
          overDue: assignedUserOverdueMap.get(normalizeName(item.name)) ?? 0,
          color: user?.userProfile.color,
        };
      }),
    [assignedUserOverdueMap, dashboard.assignedUserOpenWorkCount, usersByName],
  );

  const chartDataWorkType = useMemo<ChartBarItem[]>(
    () =>
      dashboard.workTypeOpenWorkCount.map((item) => {
        const workType = workTypesByName.get(normalizeName(item.name));

        return {
          id: workType?.id,
          name: item.name,
          open: item.count,
          overDue: workTypeOverdueMap.get(normalizeName(item.name)) ?? 0,
        };
      }),
    [dashboard.workTypeOpenWorkCount, workTypeOverdueMap, workTypesByName],
  );

  const workTypeYAxisWidth = useMemo(() => {
    if (chartDataWorkType.length === 0) {
      return 0;
    }

    const longestNameLength = Math.max(
      ...chartDataWorkType.map((item) => item.name.length),
    );

    return Math.max(longestNameLength * 10, 90);
  }, [chartDataWorkType]);

  const statusCountByName = useMemo(() => {
    const statusCountMap = new Map<string, number>();

    (workTypeStatuses || []).forEach((status) => {
      const subStatusTotal = status.subStatuses.reduce(
        (sum, subStatus) => sum + subStatus.subStatusCount,
        0,
      );

      statusCountMap.set(
        status.statusName,
        subStatusTotal || status.statusCount,
      );
    });

    return statusCountMap;
  }, [workTypeStatuses]);

  const totalWorkCount =
    dashboard.openWorkCount +
    dashboard.overDueWorkCount +
    dashboard.completedWorkCount;

  const worksCompleted =
    totalWorkCount > 0
      ? Math.round((dashboard.completedWorkCount / totalWorkCount) * 100)
      : 0;

  const dataCompleted: ChartData<"doughnut"> = useMemo(
    () => ({
      datasets: [
        {
          data: [worksCompleted, 100 - worksCompleted],
          backgroundColor: ["#47DE96", "#F9FAFB"],
          borderWidth: 0,
          borderRadius: 4,
        },
      ],
    }),
    [worksCompleted],
  );

  const optionsCompleted: ChartOptions<"doughnut"> = useMemo(
    () => ({
      cutout: "83.3%",
      rotation: -90,
      circumference: 180,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: false,
        },
      },
    }),
    [],
  );

  const dataWorks: ChartData<"doughnut"> = useMemo(
    () => ({
      datasets: [
        {
          data: STATUS_CONFIGS.map(
            (status) => dashboard.statusCount?.[status.name] ?? 0,
          ),
          backgroundColor: STATUS_CONFIGS.map((status) => status.color),
          borderWidth: 0,
        },
      ],
    }),
    [dashboard.statusCount],
  );

  const optionsWorks: ChartOptions<"doughnut"> = useMemo(
    () => ({
      cutout: "83.3%",
      rotation: -135,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
      },
    }),
    [],
  );

  const handleNavigateToStatus = useCallback(
    (statusName: string) => {
      const params = new URLSearchParams({
        workType: arrayFilter.type?.value || "all",
        assignee: arrayFilter.user?.value || "all",
        statusName,
      });

      navigate(`/dashboard/works?${params.toString()}`);
    },
    [arrayFilter.type?.value, arrayFilter.user?.value, navigate],
  );

  const handleClearFilters = () => {
    setArrayFilter({
      type: null,
      user: null,
    });
  };

  const hasActiveFilters = Boolean(
    arrayFilter?.type?.value || arrayFilter?.user?.value,
  );

  const isAllStatusesOpen = isOpenStat.length === STATUS_CONFIGS.length;

  return (
    <div>
      <FormSection name={""} extraCx={"mb-0 gap-0 px-0 py-0 shadow-box"}>
        <div className={"flex h-[200px]"}>
          <SummaryCard
            title={"Current"}
            value={dashboard.openWorkCount}
            withBorder
          />

          <SummaryCard
            title={"Overdue"}
            value={dashboard.overDueWorkCount}
            withBorder
          />

          <SummaryCard
            title={"Completed"}
            value={dashboard.completedWorkCount}
          />
        </div>
      </FormSection>

      <div className={"flex gap-6"}>
        <FormSection
          name={""}
          extraCx={"mt-6 h-fit w-[40%] px-0 shadow-box"}
          showHeading={false}>
          <div className={"px-8 text-lg font-bold"}>Work by Status</div>

          <div className={"flex w-full items-center justify-between px-8"}>
            <div className={"flex w-[400px] items-center gap-4 pb-2"}>
              <div className={"max-h-[200px] min-w-[150px]"}>
                <div className={"text-base"}>Assignee</div>

                <CustomSelect
                  value={arrayFilter.user}
                  options={usersOptions}
                  placeholder={"All"}
                  onChange={(newValue) => {
                    setArrayFilter((prev) => ({
                      ...prev,
                      user: newValue as SelectOptionType | null,
                    }));
                  }}
                  selectStyle={{ listHeight: "250px" }}
                  withMenu
                />
              </div>

              <div className={"max-h-[200px] min-w-[150px]"}>
                <div className={"text-base"}>Work Type</div>

                <CustomSelect
                  options={workTypeOptions}
                  value={arrayFilter.type}
                  placeholder={"All"}
                  onChange={(newValue) => {
                    setArrayFilter((prev) => ({
                      ...prev,
                      type: newValue as SelectOptionType | null,
                    }));
                  }}
                  withMenu
                  selectStyle={{ listHeight: "250px" }}
                />
              </div>

              {hasActiveFilters && (
                <Button
                  label={"Clear all"}
                  onClick={handleClearFilters}
                  extraClasses={
                    "normal-case py-3 px-2 max-h-[42px] mt-[15px] min-h-fit h-fit"
                  }
                  colorType={"ghost"}
                />
              )}
            </div>

            <div className={"mt-2 flex cursor-pointer gap-1"}>
              <div
                className={
                  "flex h-[34px] w-[30px] items-center justify-center rounded-[10px] bg-[#ede6f6]"
                }>
                {isAllStatusesOpen ? (
                  <TbArrowsDiagonalMinimize
                    size={"18px"}
                    className={"rotate-45"}
                    color={"#7a22b1"}
                    onClick={() => {
                      setIsOpenStat([]);
                    }}
                  />
                ) : (
                  <TbArrowsDiagonal2
                    size={"18px"}
                    className={"rotate-45"}
                    color={"#7a22b1"}
                    onClick={() => {
                      setIsOpenStat(
                        STATUS_CONFIGS.map((status) => status.mainName),
                      );
                    }}
                  />
                )}
              </div>
            </div>
          </div>

          {isLoadingStatuses ? (
            <LottieLoading />
          ) : (
            (workTypeStatuses || []).length > 0 &&
            STATUS_CONFIGS.map((stat) => (
              <div key={stat.name} className={"px-2"}>
                <div
                  onClick={() => {
                    setIsOpenStat((prev) =>
                      prev.includes(stat.mainName)
                        ? prev.filter((name) => name !== stat.mainName)
                        : [...prev, stat.mainName],
                    );
                  }}
                  className={
                    "flex cursor-pointer items-center justify-between rounded-md px-6 py-2 hover:bg-gray-100"
                  }>
                  <div className={"flex items-center gap-2"}>
                    <div
                      className={"h-3 w-3 rounded-full"}
                      style={{ backgroundColor: stat.color }}
                    />

                    <div className={"text-sm font-bold"}>{stat.mainName}</div>
                  </div>

                  <div className={"flex items-center gap-1"}>
                    <div className={"text-sm"}>
                      {statusCountByName.get(stat.name) ?? 0}
                    </div>

                    <div>
                      {isOpenStat.includes(stat.mainName) ? (
                        <IoIosArrowUp />
                      ) : (
                        <IoIosArrowDown />
                      )}
                    </div>
                  </div>
                </div>

                {isOpenStat.includes(stat.mainName) &&
                  (workTypeStatuses || [])
                    .filter((item) => item.statusName === stat.name)
                    .map((item) => {
                      const mainStatusCount =
                        item.subStatuses.find(
                          (sub) => sub.subStatusName === "NA",
                        )?.subStatusCount ?? item.statusCount;

                      return (
                        <div
                          key={item.statusName}
                          className={
                            "mt-[20px] border-b-[1px] border-[#D2D4D7] first:mt-0"
                          }>
                          <div
                            onClick={() => {
                              handleNavigateToStatus(item.statusName);
                            }}
                            className={
                              "hover:bg-purple flex cursor-pointer items-center justify-between rounded-[7px] px-6 py-[14px] text-[14px] hover:text-white"
                            }>
                            <div className={"flex items-center gap-2 pl-3"}>
                              <div
                                className={"h-3 w-3 rounded-full"}
                                style={{ backgroundColor: stat.color }}
                              />

                              <div>{item.statusName}</div>
                            </div>

                            <div>{mainStatusCount}</div>
                          </div>

                          <div className={"mb-[15px]"}>
                            {item.subStatuses
                              .filter((sub) => sub.subStatusName !== "NA")
                              .map((sub) => (
                                <div
                                  key={sub.subStatusName}
                                  onClick={() => {
                                    handleNavigateToStatus(sub.subStatusName);
                                  }}
                                  className={
                                    "hover:bg-purple flex cursor-pointer items-center justify-between rounded-[7px] px-6 py-[14px] text-[14px] hover:text-white"
                                  }>
                                  <div
                                    className={"flex items-center gap-2 pl-3"}>
                                    <div
                                      className={"h-3 w-3 rounded-full"}
                                      style={{ backgroundColor: stat.color }}
                                    />

                                    <div>{sub.subStatusName}</div>
                                  </div>

                                  <div>{sub.subStatusCount}</div>
                                </div>
                              ))}
                          </div>
                        </div>
                      );
                    })}
              </div>
            ))
          )}
        </FormSection>

        <div className={"w-[60%]"}>
          <div className={"flex space-x-6"}>
            <FormSection
              name={""}
              extraCx={"my-0 w-[60%] gap-0 px-[0px] py-0 pr-[70px] shadow-box"}>
              <div className={"flex items-center justify-around text-[14px]"}>
                <div className={"mx-[40px] my-[40px] h-[200px] w-[200px]"}>
                  <Doughnut data={dataWorks} options={optionsWorks} />
                </div>

                <div>
                  {STATUS_CONFIGS.map((status) => (
                    <div
                      key={status.name}
                      className={"mb-[15px] flex items-center justify-between"}>
                      <div className={"flex items-center"}>
                        <div
                          className={"mr-[6px] h-[12px] w-[12px] rounded-full"}
                          style={{ backgroundColor: status.color }}
                        />

                        <div
                          className={
                            status.name === "Completed" ? "w-[120px]" : ""
                          }>
                          {status.mainName}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </FormSection>

            <FormSection
              name={""}
              extraCx={
                "relative my-0 flex w-[40%] items-center justify-center px-0 py-0 shadow-box"
              }>
              <div
                className={
                  "z-2 relative flex w-[200px] flex-col items-center justify-center gap-2"
                }>
                <div
                  className={
                    "mb-6 flex items-center gap-2 text-[16px] font-bold text-[#1F2937]"
                  }>
                  <div>Completion Rate</div>
                  <InfoTooltip message={getInfoMessage("Completion Rate")} />
                </div>

                <div className={"relative z-0 h-[200px] w-[200px]"}>
                  <div
                    className={
                      "pointer-events-none relative z-0 mt-[-50px] h-[200px] w-[200px]"
                    }>
                    <Doughnut data={dataCompleted} options={optionsCompleted} />
                  </div>

                  <div
                    className={
                      "relative bottom-[80px] left-[52px] flex h-[30px] w-[100px] items-center justify-center text-[30px] font-[700]"
                    }>
                    {worksCompleted}%
                  </div>
                </div>
              </div>
            </FormSection>
          </div>

          <FormSection
            name={""}
            extraCx={"mt-0 pt-0 pb-0 shadow-box"}
            showHeading={false}>
            {chartDataTeam.length > 0 && (
              <div className={"h-fit py-10"}>
                <div className={"flex items-center justify-between"}>
                  <div className={"text-[18px] font-bold"}>Team</div>

                  <div className={"flex space-x-[24px]"}>
                    <div className={"flex items-center space-x-[6px]"}>
                      <div
                        className={
                          "h-[12px] w-[12px] rounded-full bg-[#166AD9]"
                        }
                      />

                      <div className={"text-[14px]"}>Current</div>
                    </div>

                    <div className={"flex items-center space-x-[6px]"}>
                      <div
                        className={
                          "h-[12px] w-[12px] rounded-full bg-[#FFD80B]"
                        }
                      />

                      <div className={"text-[14px]"}>Overdue</div>
                    </div>
                  </div>
                </div>

                <ResponsiveContainer
                  width={"100%"}
                  height={chartDataTeam.length * 70}>
                  <BarChart
                    style={{ width: "100%", cursor: "pointer" }}
                    data={chartDataTeam}
                    onClick={(data) => {
                      const payload = data?.activePayload?.[0]?.payload;

                      if (!payload?.id) {
                        return;
                      }

                      const params = new URLSearchParams({
                        userId: payload.id,
                        username: payload.name,
                      });

                      navigate(`/works/all?${params.toString()}`);
                    }}
                    layout={"vertical"}>
                    <XAxis
                      type={"number"}
                      stroke={"#c2c2c2"}
                      tick={({ x, y, payload }) => (
                        <g transform={`translate(${x},${y + 5})`}>
                          <text
                            x={0}
                            y={0}
                            dy={4}
                            fontSize={12}
                            textAnchor={"middle"}
                            fill={"black"}>
                            {payload.value}
                          </text>
                        </g>
                      )}
                    />

                    <CartesianGrid
                      stroke={"#c2c2c2"}
                      strokeWidth={0.5}
                      horizontal={false}
                    />

                    <YAxis
                      tickLine={false}
                      type={"category"}
                      stroke={"#c2c2c2"}
                      dataKey={"name"}
                      tick={({ x, y, payload }) => {
                        const color =
                          chartDataTeam[payload.index]?.color || "#166AD9";

                        return (
                          <g transform={`translate(${x - 20},${y})`}>
                            <circle r={20} fill={color} />

                            <text
                              x={0}
                              y={0}
                              dy={4}
                              fontSize={14}
                              textAnchor={"middle"}
                              fill={"white"}>
                              {getInitialsFromFullName(payload.value || "")}
                            </text>
                          </g>
                        );
                      }}
                    />

                    <CartesianGrid strokeDasharray={"0 3"} />
                    <Tooltip content={<CustomTooltip />} />

                    <Bar
                      stackId={"a"}
                      dataKey={"open"}
                      fill={"#166AD9"}
                      barSize={10}
                      radius={3}>
                      <LabelList
                        dataKey={"name"}
                        position={"insideTopLeft"}
                        fontSize={16}
                        dy={-24}
                        textAnchor={"start"}
                        width={0}
                      />
                    </Bar>

                    <Bar
                      stackId={"a"}
                      dataKey={"overDue"}
                      fill={"#FFD80B"}
                      radius={3}
                      barSize={10}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </FormSection>

          <FormSection
            name={""}
            extraCx={"pt-0 pb-0 shadow-box"}
            showHeading={false}>
            {workTypeYAxisWidth > 0 && chartDataWorkType.length > 0 && (
              <div className={"h-fit py-10"}>
                <div className={"flex items-center justify-between"}>
                  <div className={"text-[18px] font-bold"}>Work Type</div>

                  <div className={"flex space-x-[24px]"}>
                    <div className={"flex items-center space-x-[6px]"}>
                      <div
                        className={
                          "h-[12px] w-[12px] rounded-full bg-[#166AD9]"
                        }
                      />

                      <div className={"text-[14px]"}>Current</div>
                    </div>

                    <div className={"flex items-center space-x-[6px]"}>
                      <div
                        className={
                          "h-[12px] w-[12px] rounded-full bg-[#FFD80B]"
                        }
                      />

                      <div className={"text-[14px]"}>Overdue</div>
                    </div>
                  </div>
                </div>

                <ResponsiveContainer
                  width={"100%"}
                  height={chartDataWorkType.length * 80}>
                  <BarChart
                    style={{ width: "100%", cursor: "pointer" }}
                    data={chartDataWorkType}
                    layout={"vertical"}
                    onClick={(data) => {
                      const payload = data?.activePayload?.[0]?.payload;

                      if (!payload) {
                        return;
                      }

                      const params = new URLSearchParams({
                        workType: payload.name || "",
                        workTypeId: payload.id || "",
                      });

                      navigate(
                        `/dashboard/works-by-work-type?${params.toString()}`,
                      );
                    }}>
                    <XAxis
                      type={"number"}
                      stroke={"#c2c2c2"}
                      tick={({ x, y, payload }) => (
                        <g transform={`translate(${x},${y + 5})`}>
                          <text
                            x={0}
                            y={0}
                            dy={4}
                            fontSize={12}
                            textAnchor={"middle"}
                            fill={"black"}>
                            {payload.value}
                          </text>
                        </g>
                      )}
                    />

                    <CartesianGrid
                      stroke={"#c2c2c2"}
                      strokeWidth={0.5}
                      horizontal={false}
                    />

                    <YAxis
                      tickLine={false}
                      type={"category"}
                      stroke={"#5a5a5a"}
                      dataKey={"name"}
                      width={workTypeYAxisWidth}
                    />

                    <CartesianGrid strokeDasharray={"0 3"} />
                    <Tooltip content={<CustomTooltip />} />

                    <Bar
                      stackId={"a"}
                      dataKey={"open"}
                      fill={"#166AD9"}
                      barSize={30}
                      radius={4}
                    />

                    <Bar
                      stackId={"a"}
                      dataKey={"overDue"}
                      fill={"#FFD80B"}
                      radius={4}
                      barSize={30}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </FormSection>
        </div>
      </div>
    </div>
  );
};
